import { reactQueryFetcherAdapter, reactQueryInfiniteFetcherAdapter } from "../reactQueryFetcherAdapters";
import { createAddItemsToCache, getFindEndpoint, getSearchEndpoint } from "./cacheHelpers";
import { createFindFetcher } from "./createFindFetcher";
import { createSearchFetcher } from "./createSearchFetcher";
import type { ProviderFilters, ProviderItem, ProviderType, Provider } from "./providers";
import { mapProviders } from "./providers";
import type { SearchParamsWithoutPageIndex, SearchResult } from "./types";
import { isRemoteLookupFindResult } from "./validators";

const adaptFindFetcher = <T extends ProviderType>(type: T, provider: Provider<T>) => {
  const fetcher = createFindFetcher(type, provider);
  return reactQueryFetcherAdapter(getFindEndpoint(type), fetcher, isRemoteLookupFindResult);
};

const adaptSearchFetcher = <T extends ProviderType>(type: T, provider: Provider<T>) => {
  const fetcher = createSearchFetcher(type, provider);
  const addItemsToCache = createAddItemsToCache(type);
  return reactQueryInfiniteFetcherAdapter(
    getSearchEndpoint(type),
    async (
      signal: AbortSignal,
      [params]: [SearchParamsWithoutPageIndex<ProviderFilters<T>>],
      pageIndex = 0
    ): Promise<SearchResult<ProviderItem<T>>> => {
      const result = await fetcher(signal, { ...params, page_index: pageIndex });
      try {
        if (!signal.aborted) {
          addItemsToCache(params.app_group_id, result.items);
        }
      } catch {
        // Swallow any errors that occur from adding to cache.
      }
      return result;
    }
  );
};

export const remoteLookupFindFetchers = mapProviders<{
  [T in ProviderType]: ReturnType<typeof adaptFindFetcher<T>>;
}>(adaptFindFetcher);

export const remoteLookupSearchFetchers = mapProviders<{
  [T in ProviderType]: ReturnType<typeof adaptSearchFetcher<T>>;
}>(adaptSearchFetcher);
